import { useState, useCallback, useEffect } from 'react';

/**
 * Read a filename out of a Content-Disposition header (attachment; filename="foo.csv").
 * Falls back to a generic name when parsing fails.
 */
function parseFilename(contentDisposition, format) {
  if (contentDisposition) {
    const starMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i);
    if (starMatch && starMatch[1]) {
      try {
        return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, ''));
      } catch {
        /* fallthrough */
      }
    }
    const plainMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim();
    }
  }
  return `urbanize_projects.${format}`;
}

/**
 * Trigger a browser download for a given Blob without leaving the page.
 */
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Hook for fetching the user's monthly export quota and performing exports.
 *
 * Safe for non-subscribers: operations short-circuit locally so the UI
 * never surfaces a 403 when the user isn't Pro. The backend enforces Pro
 * access independently (defense-in-depth).
 *
 * @param {boolean} isSubscriber
 */
export function useExport(isSubscriber) {
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState(null);

  const fetchQuota = useCallback(async () => {
    if (!isSubscriber) {
      setQuota(null);
      return null;
    }

    setQuotaLoading(true);
    setQuotaError(null);
    try {
      const response = await fetch('/api/exports/quota', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 403) {
          setQuota(null);
          return null;
        }
        throw new Error('Failed to fetch export quota');
      }
      const data = await response.json();
      setQuota(data.quota || null);
      return data.quota || null;
    } catch (err) {
      console.error('Error fetching export quota:', err);
      setQuotaError(err.message);
      return null;
    } finally {
      setQuotaLoading(false);
    }
  }, [isSubscriber]);

  const exportData = useCallback(
    async ({ filters, projectNids, format, name = null, savedSearchId = null }) => {
      if (!isSubscriber) {
        return { success: false, error: 'Pro subscription required' };
      }
      if (!Array.isArray(projectNids) || projectNids.length === 0) {
        return { success: false, error: 'No projects selected for export' };
      }
      if (!['csv', 'json', 'xlsx'].includes(format)) {
        return { success: false, error: 'Unsupported format' };
      }

      try {
        const response = await fetch('/api/exports', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
          },
          body: JSON.stringify({
            filters: filters || {},
            project_nids: projectNids,
            format,
            name,
            saved_search_id: savedSearchId,
          }),
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch {
            /* ignore */
          }
          if (response.status === 429) {
            if (errorData.quota) setQuota(errorData.quota);
            return {
              success: false,
              error: 'quota_exceeded',
              message: errorData.message || 'Monthly export limit reached.',
            };
          }
          if (response.status === 403) {
            return { success: false, error: 'Pro subscription required' };
          }
          throw new Error(errorData.message || `Export failed (${response.status})`);
        }

        const headers = response.headers;
        const exportCount = parseInt(headers.get('X-Export-Count') || '0', 10);
        const requestedCount = parseInt(headers.get('X-Export-Requested') || `${projectNids.length}`, 10);
        const remaining = parseInt(headers.get('X-Quota-Remaining') || '0', 10);
        const limit = parseInt(headers.get('X-Quota-Limit') || '0', 10);
        const resetsAt = parseInt(headers.get('X-Quota-Resets-At') || '0', 10);
        const filename = parseFilename(headers.get('Content-Disposition'), format);

        const blob = await response.blob();
        triggerBlobDownload(blob, filename);

        if (Number.isFinite(remaining) && Number.isFinite(limit) && limit > 0) {
          setQuota((prev) => ({
            ...(prev || {}),
            used: limit - remaining,
            limit,
            remaining,
            resets_at: resetsAt || prev?.resets_at || 0,
            days_remaining: prev?.days_remaining ?? 0,
          }));
        } else {
          fetchQuota();
        }

        return {
          success: true,
          exportCount,
          requestedCount,
          truncated: exportCount < requestedCount,
          filename,
          remaining,
        };
      } catch (err) {
        console.error('Error exporting data:', err);
        return { success: false, error: err.message };
      }
    },
    [isSubscriber, fetchQuota],
  );

  useEffect(() => {
    if (isSubscriber) {
      fetchQuota();
    } else {
      setQuota(null);
    }
  }, [isSubscriber, fetchQuota]);

  return {
    quota,
    quotaLoading,
    quotaError,
    fetchQuota,
    exportData,
  };
}

export default useExport;
